// FILE: src/shared/lib/region-utils.ts
// VERSION: 2.0.0
// CONTRACT:
// PURPOSE: Нормализация названий регионов из ЕГРЮЛ + slug-ификация для URL /rating/[region]
// SCOPE: [region, rating, filters, seo_companies, slug, routing]
// KEYWORDS: [REGION(X): normalize, clean, deduplicate, slug, transliterate]
// LINKS: [USES(region-slug-dictionary.ts): REGION_SLUG_DICTIONARY; SYNC_WITH(supabase/migrations/20260618180000_rating_region_slug.sql): slugify_region()]

import { REGION_SLUG_DICTIONARY } from './region-slug-dictionary';

/**
 * Нормализует сырое название региона из ЕГРЮЛ к читаемому формату.
 *
 * Примеры:
 *   "г. Москва"        → "Москва"
 *   "Г МОСКВА"         → "Москва"
 *   "МОСКОВСКАЯ ОБЛ."  → "Московская область"
 *   "обл. Свердловская" → "Свердловская область"
 *   "край. Краснодарский" → "Краснодарский край"
 *   "Респ. Башкортостан"  → "Башкортостан"
 *   "АО. Ханты-Мансийский Автономный округ - Югра" → "Ханты-Мансийский АО — Югра"
 */
export function normalizeRegion(raw: string): string {
  if (!raw) return raw;

  let s = raw.trim();

  // Убираем префиксы (г., г, Г., обл., край., Респ., АО. и т.д.)
  s = s
    .replace(/^[гГ]\.?\s+/, '')           // "г. Москва" / "г Москва" / "Г МОСКВА"
    .replace(/^обл\.?\s+/i, '')           // "обл. Свердловская"
    .replace(/^край\.?\s+/i, '')          // "край. Краснодарский"
    .replace(/^Респ\.?\s+/i, '')          // "Респ. Башкортостан"
    .replace(/^АО\.?\s+/i, '')            // "АО. Ханты-Мансийский..."
    .replace(/^г\.?\s+/i, '');            // ещё раз для надёжности

  // Убираем суффиксы (обл., край и т.д.) — потом добавим правильные
  const hasOblast = /обл\.?\s*$/i.test(s) || /область$/i.test(s);
  const hasKrai = /край\.?\s*$/i.test(s);
  const hasRespublika = /^респ\.?\s+/i.test(s);

  s = s
    .replace(/\s*обл\.?\s*$/i, '')
    .replace(/\s*область\s*$/i, '')
    .replace(/\s*край\.?\s*$/i, '')
    .replace(/\s*край\s*$/i, '')
    .replace(/^респ\.?\s+/i, '')
    .replace(/^республика\s+/i, '')
    .trim();

  // Приводим к нормальному регистру: первая буква заглавная, остальные строчные
  s = s
    .split(/[\s-]+/)
    .map(w => {
      if (w.length <= 2) return w.toUpperCase(); // РФ, АО, ХМАО и т.п.
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    })
    .join(' ')
    .replace(/\s*-\s*/g, ' — ')  // дефисы → тире
    .replace(/\s+/g, ' ');       // схлопываем множественные пробелы

  // Специальные случаи
  if (/автоном/i.test(s) && /округ/i.test(s)) {
    s = s.replace(/автономный округ/i, 'АО').replace(/ - /g, ' — ').trim();
  }
  if (/ханты.мансийск/i.test(s)) s = 'Ханты-Мансийский АО — Югра';
  if (/ямало.ненец/i.test(s)) s = 'Ямало-Ненецкий АО';

  // Добавляем тип региона обратно
  if (hasRespublika) {
    if (!s.match(/^(республика|респ)/i)) s = s + ' (Республика)';
  } else if (hasKrai) {
    s = s + ' край';
  } else if (hasOblast && !/область/i.test(s)) {
    s = s + ' область';
  }

  // Карта известных сокращений
  const CANONICAL: Record<string, string> = {
    'Москва': 'Москва',
    'МОСКВА': 'Москва',
    'Санкт-Петербург': 'Санкт-Петербург',
    'САНКТ-ПЕТЕРБУРГ': 'Санкт-Петербург',
    'Севастополь': 'Севастополь',
  };

  const upper = s.toUpperCase();
  for (const [key, val] of Object.entries(CANONICAL)) {
    if (upper === key.toUpperCase()) return val;
  }

  return s;
}

/**
 * Нормализует регион для поиска — возвращает массив возможных вариантов
 * для использования в .or() фильтре.
 */
export function normalizeRegionForSearch(raw: string): string {
  // Для поиска: убираем префиксы и приводим к нижнему регистру
  return normalizeRegion(raw).toLowerCase();
}

// ============================================================================
// #region Slug
// ============================================================================

/**
 * Таблица транслитерации кириллицы в латиницу.
 * Синхронизирована с public.slugify_region() в миграции 20260618180000.
 */
const CYRILLIC_TO_LATIN: Record<string, string> = {
  'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd',
  'е': 'e', 'ё': 'yo', 'ж': 'zh', 'з': 'z', 'и': 'i',
  'й': 'y', 'к': 'k', 'л': 'l', 'м': 'm', 'н': 'n',
  'о': 'o', 'п': 'p', 'р': 'r', 'с': 's', 'т': 't',
  'у': 'u', 'ф': 'f', 'х': 'kh', 'ц': 'ts', 'ч': 'ch',
  'ш': 'sh', 'щ': 'sch', 'ъ': '', 'ы': 'y', 'ь': '',
  'э': 'e', 'ю': 'yu', 'я': 'ya',
};

/**
 * Преобразует нормализованное имя региона в URL-совместимый slug.
 *
 * Алгоритм соответствует public.slugify_region() из миграции:
 * 1. Нормализация имени через normalizeRegion()
 * 2. Транслитерация кириллицы → латиница
 * 3. lowerCase
 * 4. Замена НЕ [a-z0-9] на дефис
 * 5. Схлопывание множественных дефисов
 * 6. Обрезка ведущих/замыкающих дефисов
 * 7. Удаление транслитерированных адм. префиксов (g-, obl-, krai-, resp-)
 * 8. Ограничение 120 символов
 *
 * @param name — имя региона в любом формате (ЕГРЮЛ, человеческий)
 * @returns slug (например, "Москва" → "moskva", "г. Санкт-Петербург" → "sankt-peterburg")
 */
export function regionToSlug(name: string): string {
  // ponytail: ступень 6 — специфическая транслитерация, stdlib не покрывает
  if (!name) return '';

  // Шаг 1: нормализация имени
  const normalized = normalizeRegion(name);

  // Шаг 2: transliterate
  const lower = normalized.toLowerCase();
  let result = '';
  for (const char of lower) {
    result += CYRILLIC_TO_LATIN[char] ?? char;
  }

  // Шаг 3: replace non [a-z0-9] with hyphen
  result = result.replace(/[^a-z0-9]/g, '-');

  // Шаг 4: collapse multiple hyphens
  result = result.replace(/-{2,}/g, '-');

  // Шаг 5: trim leading/trailing hyphens
  result = result.replace(/^-+/, '').replace(/-+$/, '');

  // Шаг 6: remove common administrative prefixes AND suffixes after transliteration
  // normalizeRegion() may re-add suffixes (область→oblast, край→krai) — strip them for clean slugs
  result = result
    .replace(/^g-/, '').replace(/^obl-/, '').replace(/^krai-/, '').replace(/^resp-/, '')
    .replace(/-oblast$/, '').replace(/-krai$/, '').replace(/-respublika$/, '');

  // Шаг 7: re-trim hyphens (may remain after suffix stripping)
  result = result.replace(/^-+/, '').replace(/-+$/, '');

  // Шаг 8: limit to 120 chars
  if (result.length > 120) {
    result = result.slice(0, 120);
  }

  return result;
}

/**
 * Преобразует slug обратно в нормализованное имя региона.
 * Использует статический словарь REGION_SLUG_DICTIONARY (без обращения к БД).
 *
 * @param slug — slug региона (например, "moskva")
 * @returns нормализованное имя региона или null, если slug не найден
 */
export function slugToRegion(slug: string): string | null {
  // ponytail: ступень 6 — требуется словарь, которого нет в stdlib
  if (!slug) return null;
  const normalizedSlug = slug.toLowerCase().replace(/^-+|-+$/g, '');
  return REGION_SLUG_DICTIONARY[normalizedSlug] ?? null;
}

// #endregion Slug
